import { toast } from "react-toastify"

import { Button, NumericInput } from "~/components/design-system/components"
import { cn } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"
import {
  MAX_NO_RECENT_SALES_DAYS,
  defaultRateOfSaleWeights,
  minDaysExceedsWindow,
  rateOfSalePeriodLabel,
  rateOfSalePeriods,
  rateOfSaleWeightsFor,
  visibleRateOfSaleWeights,
  type RateOfSaleWeights,
} from "~/utils/rateOfSale"
import { showErrorToast } from "~/utils/toaster"
import {
  useRateOfSaleMutations,
  useRateOfSaleSettings,
  type RateOfSaleSettings,
} from "../useAllocationData"
import { DraftCommandbar, type SaveCallbacks } from "../DraftCommandbar"
import { TooLittleHistoryCard } from "./ReferenceRosChain"
import {
  isFlagOnlyChange,
  noteRunAfterCurrent,
  rateOfSaleChanges,
  toRateOfSaleInput,
  useSetupDrafts,
  type RateOfSaleDraft,
} from "./setupDrafts"
import { SectionCard } from "./SetupRules"
import { WeightsBar } from "./WeightsBar"

/**
 * Settings → Rate of sale: company-wide ROS settings. Sales history window,
 * sales period weights, minimum days with inventory and the Reference ROS levels.
 * Everything here applies to every rule.
 */

const WINDOW_OPTIONS = [7, 14, 28]

export default function SetupRateOfSale() {
  const { settings: saved, isLoading } = useRateOfSaleSettings()
  const { saveSettings } = useRateOfSaleMutations()
  const { rateOfSaleDraft: draft, setRateOfSaleDraft: setDraft } = useSetupDrafts()

  if (isLoading || !saved) {
    return (
      <div className="paragraph-sm p-6 text-fg-muted">
        {`Loading rate of ${formatName("sale")} settings…`}
      </div>
    )
  }

  const settings: RateOfSaleSettings = { ...saved, ...draft }
  const changes = rateOfSaleChanges(saved, draft)
  const saving = saveSettings.isPending

  const stage = (patch: RateOfSaleDraft) => {
    if (saving) return
    setDraft((prev) => ({ ...prev, ...patch }))
  }

  const save = (callbacks?: SaveCallbacks) =>
    saveSettings.mutate(toRateOfSaleInput(settings), {
      onSuccess: ({ run }) => {
        setDraft({})
        toast.success(`Rate of ${formatName("sale")} settings saved`)
        noteRunAfterCurrent(run)
        callbacks?.onSaved?.()
      },
      onError: (error) => {
        showErrorToast(error)
        callbacks?.onFailed?.()
      },
    })

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex max-w-5xl flex-col gap-3.5">
        <SectionCard title="Calculation method">
          <div className="flex flex-col gap-3.5 p-3.5">
            <WindowAndWeightsCard settings={settings} disabled={saving} onCommit={stage} />
            <TooLittleHistoryCard
              settings={settings}
              edited={changes.some((change) => !isFlagOnlyChange(change.key))}
              disabled={saving}
              onCommit={stage}
            />
          </div>
        </SectionCard>
        <SectionCard title="Line flags">
          <div className="p-3.5">
            <NoRecentSalesCard
              noRecentSalesDays={settings.noRecentSalesDays}
              disabled={saving}
              onCommit={(noRecentSalesDays) => stage({ noRecentSalesDays })}
            />
          </div>
        </SectionCard>
      </div>
      <DraftCommandbar
        changes={changes}
        saving={saving}
        saveDisabledReason={
          minDaysExceedsWindow(settings)
            ? "Minimum days with inventory is longer than the sales history window."
            : undefined
        }
        onDiscard={() => setDraft({})}
        onSave={save}
      />
    </div>
  )
}

// ── Sales history window + period weights ────────────────────────────────────

function WindowAndWeightsCard({
  settings,
  disabled,
  onCommit,
}: {
  settings: RateOfSaleSettings
  disabled: boolean
  onCommit: (patch: Partial<RateOfSaleWeights & { windowDays: number }>) => void
}) {
  // Same periods the rate of sale calculation uses.
  const periods = rateOfSalePeriods(settings.windowDays)
  const weights = visibleRateOfSaleWeights(settings, settings.windowDays)
  const defaults = defaultRateOfSaleWeights(settings.windowDays)
  const isDefault = visibleRateOfSaleWeights(defaults, settings.windowDays).every(
    (weight, index) => weight === weights[index],
  )

  return (
    <div className="overflow-hidden rounded-lg border border-base bg-base">
      <div className="flex items-center gap-3 border-b border-base px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="paragraph-sm font-medium text-fg-base">
            {`${formatName("sale", true, true)} history window`}
          </div>
          <div className="paragraph-xs mt-0.5 text-fg-subtle">
            {`How far back ${formatName("sale", true)} are looked at to calculate ROS.`}
          </div>
        </div>
        <div className="flex shrink-0 gap-0.5 rounded-full bg-component p-0.5">
          {WINDOW_OPTIONS.map((days) => (
            <Button
              key={days}
              variant="transparent"
              size="xs"
              requires="edit"
              disabled={disabled}
              onClick={() => {
                if (days !== settings.windowDays) {
                  onCommit({ windowDays: days, ...defaultRateOfSaleWeights(days) })
                }
              }}
              className={cn(
                "rounded-full px-3",
                settings.windowDays === days && "bg-base font-semibold shadow-card-rest",
              )}
            >
              {days} days
            </Button>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3 px-3 py-3">
        <div className="flex items-center gap-2.5">
          <div className="min-w-0 flex-1">
            <div className="paragraph-sm font-medium text-fg-base">
              {`Weight per ${formatName("sale", true)} period`}
            </div>
            <div className="paragraph-xs mt-0.5 text-fg-subtle">
              {periods.length > 1
                ? "Drag the handles or type a percentage. The other periods adjust to keep 100%."
                : `A ${settings.windowDays}-day window has one period, so it carries all the weight.`}
            </div>
          </div>
          <Button
            variant="neutral"
            size="xs"
            requires="edit"
            disabled={isDefault || disabled}
            onClick={() => onCommit(defaults)}
          >
            Reset to default
          </Button>
        </div>
        <WeightsBar
          disabled={disabled}
          weights={weights}
          labels={periods.map(rateOfSalePeriodLabel)}
          onCommit={(next) => onCommit(rateOfSaleWeightsFor(settings.windowDays, next))}
        />
      </div>
    </div>
  )
}

function NoRecentSalesCard({
  noRecentSalesDays,
  disabled,
  onCommit,
}: {
  noRecentSalesDays: number
  disabled: boolean
  onCommit: (noRecentSalesDays: number) => void
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-base bg-base px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="paragraph-sm font-medium text-fg-base">
          {`No recent ${formatName("sale", true)}`}
        </div>
        <div className="paragraph-xs mt-0.5 max-w-[65ch] text-fg-subtle">
          {`Flags a line with a suggested qty when its store has had stock for this many days without a ${formatName("sale")} of the SKU, up to ${MAX_NO_RECENT_SALES_DAYS}. 0 turns the flag off.`}
        </div>
      </div>
      <NumericInput
        small
        requires="edit"
        disabled={disabled}
        allowZero
        max={MAX_NO_RECENT_SALES_DAYS}
        value={noRecentSalesDays}
        onChange={onCommit}
        unit="days"
        containerClassName="w-28"
        className="w-full text-right tabular-nums"
      />
    </div>
  )
}
